import { useEffect, useRef, useState } from 'react';
import {
    DocumentImage, DocumentItem, DocumentItemContent, DocumentItemName, DocumentPreview, HiddenInput, InfoCard, InfoContainer,
    InfoContent, InfoQuestion, InfoTitle, NoInfo, SubmitButton
} from './AdditionalInfo.styles';
import IMAGES from 'assets/images';
import { answerAdditionalInformationRequest, getAdditionalInformationRequest, getApplicant } from 'core/api';
import { deleteDocument, getDocumentTypes, uploadDocument } from 'core/api/underwriting';
import { useAuthorization } from 'core/context';
import { useToast } from 'core/toast';
import { Data, IAdditionalDocument, IAdditionalInformation } from 'core/utilities';
import { useHistory, useLocation } from 'react-router';
import useProgressBar from 'view/hooks/useProgressBar';
import { BackButton, DocActionButtons, InfoAnswer, PreviewContent } from './components';

const AdditionalInfo = () => {
    const history = useHistory();
    const location = useLocation();
    const { setToastMessage } = useToast();
    const authState = useAuthorization();

    const fileInputRefs = useRef<any[]>([]);

    const [ applicationId, setApplicationId ] = useState('');

    const [ currentQuestion, setCurrentQuestion ] = useState<IAdditionalInformation | null>(null);
    const [ currentAnswer, setCurrentAnswer ] = useState<string>('');
    
    const [ docRequest, setDocRequest ] = useState(false);
    const [ docQuestion, setDocQustion ] = useState('');
    const [ documents, setDocuments ] = useState<IAdditionalDocument[]>([]);
    const [ selectedDocument, setSelectedDocument ] = useState<IAdditionalDocument | null>(null);
    const [ isUploadingOrDeleting, setIsUploadingOrDeleting ] = useState(false);

    const [ dragOver, setDragOver ] = useState(false);

    const { progress } = useProgressBar(isUploadingOrDeleting);

    const goBack = () => history.push('/dashboard');

    const loadAdditionalInfoData = async () => {
        const { success, questions } = await getAdditionalInformationRequest();

        if (!success) return setToastMessage('Failed to load information');

        if (location.pathname === '/additional-info') {
            const currentQuestion = questions.filter((question) => question.answer === null && !question.question.includes('DocRequest~'))[0];
            setDocRequest(false);
            setCurrentQuestion(currentQuestion);
        } else {
            const currentQuestion = questions.filter((question) => question.answer === null && question.question.includes('DocRequest~'))[0];
            setCurrentQuestion(currentQuestion);
            setDocRequest(true);
            loadDocRequest();
            const { success, applicant } = await getApplicant(authState);
            
            if (!success) return setToastMessage('Failed to load applicant');
            setApplicationId(applicant?.applicationId);
            setSelectedDocument(null);
        }
    };

    const loadDocRequest = () => {
        if (!currentQuestion) return;

        const questionParts = currentQuestion.question.split('~');

        const mainQuestion = questionParts[1] + ':';

        const files = questionParts[2];
        const documents = JSON.parse(files);

        const currentDocs = documents.map((doc: any) => {
            const currentDoc: IAdditionalDocument = { ...doc, uploaded: false, uploadedId: '' };
            return currentDoc;
        });

        setDocQustion(mainQuestion);
        setDocuments(currentDocs);
    };

    const recordAnswer = (value: string) => {
        setCurrentAnswer(value);
    };

    const updateAdditionalInformation = async (answer: string) => {
        const { success } = await answerAdditionalInformationRequest(currentQuestion!.id, answer);

        if (!success) return setToastMessage('Something went wrong when submitting your response');

        const currentQuestionCopy: IAdditionalInformation = { ...currentQuestion! };

        currentQuestionCopy.answer = answer;

        setCurrentQuestion(currentQuestionCopy);
        setCurrentAnswer('');
    };

    const onSubmit = async () => {
        if (currentQuestion) {
            await updateAdditionalInformation(currentAnswer);
        } else {
            history.push('/dashboard');
        }
    };

    const stopReload = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
    };
    
    const onDragOver = (event: any) => {
        stopReload(event);
        setDragOver(true);
    };

    const onFileAdded = async (event : any, document: IAdditionalDocument) => {
        if (isUploadingOrDeleting || document.uploaded) return;

        const file = event.target.files[0];

        await handleFileUpload(file, document);
        event.target.files = null;
        event.target.value = '';
    };

    const onFileInputClick = (index: number, document: IAdditionalDocument) => {
        if (isUploadingOrDeleting || document.uploaded) return;
        fileInputRefs.current[index].click();
    };

    const onFileDrop = async (event: any, document: IAdditionalDocument) => {
        stopReload(event);

        if (isUploadingOrDeleting || document.uploaded) return;

        const file = event.dataTransfer.files[0];
        await handleFileUpload(file, document);

        setDragOver(false);
    };

    const enableSubmitButtonOnFileUpload = () => {
        const allFilesUploaded = documents.every(doc => doc.uploaded);

        if (allFilesUploaded) {
            let documentNames = '';
            documents.map((doc, idx) => {
                documentNames += doc.name;
                if (idx !== documents.length - 1) {
                    documentNames += ', ';
                }
            });
            setCurrentAnswer(documentNames);
        }
    };

    const editDocumentsArray = (document: IAdditionalDocument, id: string, remove=false) => {
        const doc = documents.filter(doc => doc.id === document.id)[0];
        const docIndex = documents.indexOf(doc);
        const tempDocs = documents.slice();
        tempDocs[docIndex].uploaded = remove ? false : true;
        tempDocs[docIndex].uploadedId = id;
        setDocuments(tempDocs);
    };

    const getDocumentType = async (document: IAdditionalDocument) => {
        const { success, reply } = await getDocumentTypes();
        if (!success) return setToastMessage('Failed to get document types');
        const { types } = reply as { types : Array<Data> };

        return types.filter(type => type.name === document.type)[0];
    };

    const handleFileUpload = async (file: any, document: IAdditionalDocument) => {
        setIsUploadingOrDeleting(true);

        const type = await getDocumentType(document);

        const formData = new FormData();
        formData.append('document', file);
        formData.append('_type', type?.id);
        formData.append('applicant', currentQuestion!.applicant);

        try {
            const { success, data : uploadedDocument } = await uploadDocument(applicationId, formData);
            if (!success) return setToastMessage('Failed to upload documents');

            editDocumentsArray(document, uploadedDocument.id);
            
            enableSubmitButtonOnFileUpload();
            
            setIsUploadingOrDeleting(false);
        } catch (error) {
            setToastMessage('File upload failed, please try again');
            setIsUploadingOrDeleting(false);
        }
    };

    const onDocumentDelete = async (document: IAdditionalDocument) => {
        if (isUploadingOrDeleting) return;

        setIsUploadingOrDeleting(true);

        try {
            const success = await deleteDocument(applicationId, document.uploadedId);
            if (!success) return setToastMessage('Failed to delete file');

            editDocumentsArray(document, '', true);

            setCurrentAnswer('');

            setIsUploadingOrDeleting(false);
        } catch (error) {
            setToastMessage('File upload failed, please try again');
            setIsUploadingOrDeleting(false);
        }
    };

    useEffect(() => {
        loadAdditionalInfoData();
    }, [ currentQuestion?.answer ]);

    const isCurrentAnswerEmpty = currentAnswer === '';

    return (
        <InfoContainer>
            <InfoCard>
                <BackButton goBack={goBack} />
                <InfoContent>
                    <InfoTitle>Additional Information</InfoTitle>
                    {currentQuestion ? (
                        !docRequest ? (
                            <>
                                <InfoQuestion>{currentQuestion.question}</InfoQuestion>
                                <InfoAnswer value={currentAnswer} onChange={recordAnswer} />
                                <SubmitButton disabled={isCurrentAnswerEmpty} onClick={onSubmit}>Submit</SubmitButton>
                            </>
                        ) : (
                            <>
                                <InfoQuestion>{docQuestion}</InfoQuestion>
                                {documents.map((document, index) => {
                                    const isSelected = document.id === selectedDocument?.id;
                                    return (
                                        <DocumentItem key={document.id}>
                                            <HiddenInput
                                                ref={(ref) => fileInputRefs.current[index] = ref}
                                                type="file"
                                                multiple
                                                onChange={(e) => onFileAdded(e, document)}
                                            />
                                            <DocumentItemContent onClick={() => setSelectedDocument(isSelected ? null : document)}>
                                                <DocumentImage src={IMAGES.document} height={40} width={40} />
                                                <DocumentItemName>{document.name}</DocumentItemName>
                                                <DocActionButtons document={document} isSelected={isSelected} isUploadingOrDeleting={isUploadingOrDeleting} onDelete={() => onDocumentDelete(document)} />
                                            </DocumentItemContent>
                                            {isSelected && (
                                                <DocumentPreview
                                                    selectView={true}
                                                    onDragOver={onDragOver}
                                                    onDragLeave={() => setDragOver(false)}
                                                    onClick={() => onFileInputClick(index, document)}
                                                    onDrop={(e) => onFileDrop(e, document)}
                                                >
                                                    <PreviewContent
                                                        document={document}
                                                        isUploadingOrDeleting={isUploadingOrDeleting}
                                                        dragOver={dragOver}
                                                        progress={progress}
                                                    />
                                                </DocumentPreview>
                                            )}
                                        </DocumentItem>
                                    );
                                })}
                                <SubmitButton disabled={isCurrentAnswerEmpty} onClick={onSubmit}>Continue</SubmitButton>
                            </>
                        )
                    ) : (
                        <>
                            <NoInfo>No additional information is needed at the moment</NoInfo>
                            <SubmitButton onClick={onSubmit}>Go back to dashboard</SubmitButton>
                        </>
                    )}
                </InfoContent>
            </InfoCard>
        </InfoContainer>
    );
};

export default AdditionalInfo;
